import { Monitor, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { isThemePreference, type ThemePreference } from "@/core/theme/theme";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/presentation/components/ui";

const THEME_OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  label: string;
  hint: string;
  icon: typeof Sun;
}> = [
  { value: "light", label: "Light", hint: "Bright workspace", icon: Sun },
  { value: "dark", label: "Dark", hint: "Low-light workspace", icon: Moon },
  { value: "system", label: "System", hint: "Match device setting", icon: Monitor },
];

interface ThemeToggleProps {
  compact?: boolean;
  tone?: "sidebar" | "page";
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  className?: string;
}

function ThemeToggle({
  compact = false,
  tone = "sidebar",
  side = "top",
  align = "end",
  className,
}: Readonly<ThemeToggleProps>) {
  const { preference, resolvedTheme, setPreference } = useTheme();
  const ActiveIcon = resolvedTheme === "dark" ? Moon : Sun;
  const currentLabel = THEME_OPTIONS.find((option) => option.value === preference)?.label ?? "Light";
  const isSidebar = tone === "sidebar";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={compact ? "icon" : "sm"}
          className={cn(
            compact ? "h-8 w-8" : "h-8 w-full justify-start gap-2 px-2",
            isSidebar
              ? "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            className,
          )}
          aria-label={`Theme: ${currentLabel}`}
          title="Change theme"
        >
          <ActiveIcon className="h-4 w-4 shrink-0" />
          {compact ? null : (
            <>
              <span className="flex-1 truncate text-left text-xs font-medium">Theme</span>
              <span
                className={cn(
                  "text-[10px]",
                  isSidebar ? "text-sidebar-foreground/50" : "text-muted-foreground",
                )}
              >
                {currentLabel}
              </span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-48">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Appearance</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(value) => {
            if (isThemePreference(value)) setPreference(value);
          }}
        >
          {THEME_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <DropdownMenuRadioItem key={option.value} value={option.value} className="gap-2">
                <Icon className="h-4 w-4" />
                <span className="flex flex-col">
                  <span>{option.label}</span>
                  <span className="text-[10px] text-muted-foreground">{option.hint}</span>
                </span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ThemeToggle;
